<script setup lang="ts">
import { computed } from 'vue';

import { useGlobalStyle, useTemplate } from '@/store/templateData';

import desktopImage from '../../assets/icons/icon-desktop.svg';
import mobileImage from '../../assets/icons/icon-mobile.svg';

defineOptions({
  name: 'ModuleDevicesRecommendationDisplay'
});

const { handsupConfig } = useTemplate();
const needShowBrandingClaim = computed(() => handsupConfig.value?.store?.cms_handsup_device);

const { titleStyle } = useGlobalStyle();
</script>

<template>
<div class="module-devices-recommendation-display">
  <div class="module-devices-recommendation-display__title" :style="titleStyle">
    {{ $t("cms_setting.display.devices_title") }}
  </div>
  <div class="module-devices-recommendation-display__desc">
    <div class="mb-4" v-if="needShowBrandingClaim">
      {{ $t("cms_setting.display.devices_handsup_branding_claim") }}
    </div>
    <div class="whitespace-pre-line">
      {{ $t("cms_setting.display.devices_recommendation_text") }}
    </div>
  </div>
  <div class="module-devices-recommendation-display__device-box">
    <div class="module-devices-recommendation-display__device-box__device">
      <div class="module-devices-recommendation-display__device-box__image-box">
        <el-image
          class="module-devices-recommendation-display__device-box__image-box__img"
          :src="desktopImage"
          fit="cover"
        />
        <span class="module-devices-recommendation-display__device-box__image-box__name">{{ $t("cms_setting.display.devices_desktop") }}</span>
      </div>
      <span
        class="module-devices-recommendation-display__device-box__desc"
        v-html="$t('cms_setting.display.devices_desktop_desc')"
      ></span>
    </div>
    <div class="module-devices-recommendation-display__device-box__device">
      <div class="module-devices-recommendation-display__device-box__image-box">
        <el-image
          class="module-devices-recommendation-display__device-box__image-box__img"
          :src="mobileImage"
          fit="cover"
        />
        <span class="module-devices-recommendation-display__device-box__image-box__name">{{ $t("cms_setting.display.devices_mobile") }}</span>
      </div>
      <span
        class="module-devices-recommendation-display__device-box__desc"
        v-html="$t('cms_setting.display.devices_mobile_desc')"
      ></span>
    </div>
  </div>
</div>
</template>
<style lang="scss" scoped>
$image-device-md: 117px;
$image-device-sm: 70px;
$desktop-fixed-width: 1075px;
$desc-max-width: 300px;

.module-devices-recommendation-display {
  @apply px-5 py-4;

  &__title {
    @apply mb-4 font-medium break-all;
    @apply text-4xl text-center whitespace-pre-line;

    word-wrap: break-word;
  }

  &__desc {
    @apply mb-4 text-center;
  }

  &__device-box {
    @apply flex flex-col justify-center space-y-5;

    &__device {
      @apply flex flex-col items-center justify-center;
      @apply flex-auto flex-grow-0;
    }

    &__image-box {
      @apply items-center mr-0 flex flex-col;

      &__img {
        @apply mb-3 w-full;

        width: $image-device-sm;
      }

      &__name {
        @apply text-h4 font-medium text-gray-900 leading-8 truncate;
      }
    }

    &__desc {
      @apply py-3 px-0 text-sm text-gray-900 leading-normal w-full;

      max-width: $desc-max-width;
    }
  }
}

// tablet
.tablet .module-devices-recommendation-display {
  @apply px-10;

  &__device-box {
    @apply flex-row space-y-0;

    &__device {
      @apply flex-row;
    }

    &__image-box {
      @apply mr-2 flex flex-col;

      &__img {
        width: $image-device-md;
      }
    }

    &__desc {
      @apply px-4;
    }
  }
}

// PC - 1024px
.laptop .module-devices-recommendation-display {
  @apply py-8;

  &__device-box {
    @apply flex-row space-y-0;

    &__device {
      @apply flex-row;
    }

    &__image-box {
      @apply mr-2 flex flex-col;

      &__img {
        width: $image-device-md;
      }
    }

    &__desc {
      @apply px-4;
    }
  }
}

// PC - 1440px
.desktop .module-devices-recommendation-display {
  width: $desktop-fixed-width;

  @apply py-8;

  &__title {
    @apply mb-5;
  }

  &__device-box {
    @apply flex-row space-y-0;

    &__device {
      @apply flex-row;
    }

    &__image-box {
      @apply mr-2 flex flex-col;

      &__img {
        width: $image-device-md;
      }
    }

    &__desc {
      @apply px-4;
    }
  }
}
</style>
